import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { supabase } from '@/integrations/supabase/client';
import { TourOverlay } from './TourOverlay';
import { TourCard } from './TourCard';

/**
 * TourLauncher:
 * - Auto-launches the 'welcome' tour for brand-new users (shouldAutoLaunch = true)
 * - Detects user roles and queues role-specific tour after welcome
 * - Skips tour on overlay / print routes
 * - Renders TourOverlay + TourCard when a tour is active
 */
export function TourLauncher() {
  const { shouldAutoLaunch, startTour, activeTour, isLoading, setQueuedTour } = useOnboarding();
  const location = useLocation();
  const hasAutoLaunched = useRef(false);

  // Routes where the tour should never appear
  const skipRoutes = ['/overlay', '/print', '/auth'];
  const shouldSkip = skipRoutes.some(r => location.pathname.startsWith(r));

  useEffect(() => {
    if (isLoading || shouldSkip || hasAutoLaunched.current) return;
    if (shouldAutoLaunch) {
      hasAutoLaunched.current = true;

      // Detect roles and queue role-specific tour
      const detectAndQueue = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Check roles in priority order: owner/admin > moderator > helper
        const checks = [
          { role: 'owner', tour: 'admin-onboarding' },
          { role: 'admin', tour: 'admin-onboarding' },
          { role: 'moderator', tour: 'moderator-onboarding' },
          { role: 'helper', tour: 'helper-onboarding' },
        ];

        for (const { role, tour } of checks) {
          const { data } = await supabase.rpc('has_role', {
            _user_id: user.id,
            _role: role as any,
          });
          if (data) {
            setQueuedTour(tour);
            break;
          }
        }
      };

      detectAndQueue();

      // Small delay to let the page render first
      const timer = setTimeout(() => startTour('welcome'), 800);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoLaunch, isLoading, shouldSkip, startTour, setQueuedTour]);

  if (shouldSkip || !activeTour) return null;

  return (
    <>
      <TourOverlay />
      <TourCard />
    </>
  );
}
